import moment from "moment";
import { z } from "zod";

import { addCustomFieldIssues, customFieldsSchema } from "components/BaseForm";
import { COST_ERROR, isValidCost } from "routes/product/pricing/pricing";
import { isSpecialLine } from "routes/revenue_opportunity/form/invoiceLines";

const DATE_FORMAT = "YYYY-MM-DD";
const INVALID_DATE = `Enter a valid date (${DATE_FORMAT})`;

const OPTIONAL_DATE_FIELDS = [
  "invoice_date",
  "sales_order_date",
  "launch_date",
];

const REQUIRED_FIELDS = [
  ["entity_id", "Entity is required"],
  ["status_id", "Status is required"],
  ["region_id", "Region is required"],
  ["country", "Country is required"],
];

const isBlank = value => value == null || value === "";
const isValidDate = value => moment(value, DATE_FORMAT, true).isValid();
const isPositiveInteger = value => /^\d+$/.test(String(value)) && +value >= 1;
const isWholeNumber = value => /^\d+$/.test(String(value));

const invoiceLineSchema = z.object({
  id: z.number().optional(),
  product_id: z.union([z.number(), z.string()]).nullable(),
  amount: z.string(),
  term: z.string(),
  quantity: z.string(),
  cost: z.string(),
  license_start_date: z.string().nullable(),
  license_end_date: z.string().nullable(),
  is_discount: z.boolean(),
  is_penalty: z.boolean(),
  is_total_value_override: z.boolean(),
});

const pointOfContactSchema = z.object({
  point_of_contact_id: z.number().nullable(),
  is_launch_contact: z.boolean(),
  is_reporting_contact: z.boolean(),
});

const addIssue = (ctx, path, message) =>
  ctx.addIssue({ code: z.ZodIssueCode.custom, path, message });

const addLicenseDateIssues = (line, index, ctx) => {
  const { license_start_date: start, license_end_date: end } = line;
  if (!start && end) {
    addIssue(
      ctx,
      ["invoices", index, "license_start_date"],
      "Required with an end date"
    );
  }
  if (start && !isValidDate(start)) {
    addIssue(ctx, ["invoices", index, "license_start_date"], INVALID_DATE);
  }
  if (end && !isValidDate(end)) {
    addIssue(ctx, ["invoices", index, "license_end_date"], INVALID_DATE);
  }
};

const addInvoiceIssues = (invoices, disabledProductIds, ctx) => {
  if (invoices.length === 0) {
    addIssue(ctx, ["invoices"], "Add at least one invoice line");
    return;
  }
  invoices.forEach((line, index) => {
    const path = field => ["invoices", index, field];
    if (isSpecialLine(line)) {
      if (isBlank(line.amount) || Number.isNaN(parseFloat(line.amount))) {
        addIssue(ctx, path("amount"), "Required");
      }
      return;
    }
    if (isBlank(line.product_id)) {
      addIssue(ctx, path("product_id"), "Product is required");
    } else if (disabledProductIds.has(line.product_id)) {
      addIssue(
        ctx,
        path("product_id"),
        "This product is disabled. Select an enabled product to save."
      );
    }
    if (!isPositiveInteger(line.term)) {
      addIssue(ctx, path("term"), "At least 1");
    }
    if (!isWholeNumber(line.quantity)) {
      addIssue(ctx, path("quantity"), "Whole number");
    }
    if (!isValidCost(line.cost)) {
      addIssue(ctx, path("cost"), COST_ERROR);
    }
    addLicenseDateIssues(line, index, ctx);
  });
};

const addPointOfContactIssues = (pointsOfContact, ctx) => {
  const seen = new Set();
  pointsOfContact.forEach((row, index) => {
    const path = ["points_of_contact", index, "point_of_contact_id"];
    if (row.point_of_contact_id == null) {
      addIssue(ctx, path, "Point of contact is required");
      return;
    }
    if (seen.has(row.point_of_contact_id)) {
      addIssue(ctx, path, "This point of contact is already listed");
    }
    seen.add(row.point_of_contact_id);
  });
};

const addDateIssues = (values, ctx) => {
  if (!values.effective_date) {
    addIssue(ctx, ["effective_date"], "Effective Date is required");
  } else if (!isValidDate(values.effective_date)) {
    addIssue(ctx, ["effective_date"], INVALID_DATE);
  }
  OPTIONAL_DATE_FIELDS.forEach(field => {
    if (values[field] && !isValidDate(values[field])) {
      addIssue(ctx, [field], INVALID_DATE);
    }
  });
};

/**
 * The Revenue Opportunity form schema. It carries every rule that gated the
 * legacy Save button, so the same records are accepted and rejected.
 */
export const buildRevenueOpportunitySchema = ({
  customFieldConfigs,
  disabledProductIds = new Set(),
  isPiracyEnabled,
}) =>
  z
    .object({
      entity_id: z.number().nullable(),
      campaign_id: z.number().nullable(),
      contact_id: z.number().nullable(),
      case_ids: z.array(z.number()),
      status_id: z.number().nullable(),
      effective_date: z.string().nullable(),
      region_id: z.number().nullable(),
      country: z.string().nullable(),
      assignee_id: z.number().nullable(),
      recipient_type: z.string().nullable(),
      recipient_id: z.number().nullable(),
      billing_to: z.string(),
      purchase_order: z.array(z.string()),
      invoice: z.array(z.string()),
      invoice_date: z.string().nullable(),
      sales_order: z.array(z.string()),
      sales_order_date: z.string().nullable(),
      launch_date: z.string().nullable(),
      actionable_machines: z.string(),
      comments: z.string(),
      invoices: z.array(invoiceLineSchema),
      points_of_contact: z.array(pointOfContactSchema),
      tags: z.array(z.any()),
      attachments: z.array(z.any()),
      custom_fields: customFieldsSchema,
    })
    .superRefine((values, ctx) => {
      REQUIRED_FIELDS.forEach(([field, message]) => {
        if (isBlank(values[field])) addIssue(ctx, [field], message);
      });
      addDateIssues(values, ctx);
      addInvoiceIssues(values.invoices, disabledProductIds, ctx);
      addPointOfContactIssues(values.points_of_contact, ctx);
      if (
        isPiracyEnabled &&
        values.actionable_machines !== "" &&
        !isPositiveInteger(values.actionable_machines)
      ) {
        addIssue(
          ctx,
          ["actionable_machines"],
          "Actionable Machines must be a whole number of at least 1"
        );
      }
      addCustomFieldIssues(customFieldConfigs, values.custom_fields, ctx);
    });
